import { makeAutoObservable } from "mobx";
import PaginatedEndpoint from "~/util/PaginatedEndpoint";
import notifStore from "./notifications";
import { Document, DocumentId } from "~/document";
import * as api from "~/api";
import { ReqStatus } from "~/util";

export type BookmarkId = string;
export interface BookmarkInfo {
  id: BookmarkId;
  title: string;
  user_display: string;
  document: DocumentId;
  document_data?: Document;
}

export class BookmarkStore {
  f = new PaginatedEndpoint<BookmarkInfo>("bookmarked_documents", notifStore);
  byDoc: Record<DocumentId, BookmarkInfo | null> = {};
  reqStatus: ReqStatus = ReqStatus.Initial;
  toggleReqStatus: ReqStatus = ReqStatus.Initial;

  constructor() {
    makeAutoObservable(this);
  }

  get loading() {
    return this.f.loading || this.reqStatus === ReqStatus.InProcess;
  }

  async loadFor(docId: DocumentId): Promise<BookmarkInfo | null> {
    this.reqStatus = ReqStatus.InProcess;
    try {
      const resp = await api.get(`documents/${docId}/bookmarks`);
      const bookmark = resp?.data?.id ? (resp.data as BookmarkInfo) : null;
      this.byDoc[docId] = bookmark;
      return bookmark;
    } finally {
      this.reqStatus = ReqStatus.Success;
    }
  }

  async toggleBookmarkedStatus(doc: Document, newState: boolean): Promise<BookmarkInfo | null> {
    this.toggleReqStatus = ReqStatus.InProcess;
    try {
      if (!newState) {
        const existing = this.byDoc[doc.id];
        if (existing) {
          await api.del(`bookmarked_documents/${existing.id}`);
        }
        this.byDoc[doc.id] = null;
        this.toggleReqStatus = ReqStatus.Success;
        return null;
      } else {
        const resp = await api.post("bookmarked_documents", {
          title: doc.title,
          document: doc.id,
        });
        if (resp === null) {
          this.toggleReqStatus = ReqStatus.Failed;
          return null;
        }
        this.byDoc[doc.id] = resp.data;
        this.toggleReqStatus = ReqStatus.Success;
        return resp.data;
      }
    } catch (err) {
      this.toggleReqStatus = ReqStatus.Failed;
      return null;
    }
  }

  isBookmarked(id: Document["id"]): boolean {
    return !!this.byDoc[id]?.id;
  }

  isLoadedFor(id: Document["id"]): boolean {
    return this.byDoc[id] !== undefined;
  }
}

const bookmarkStore = new BookmarkStore();

export default bookmarkStore;
